/**
 * HTTP status code constants. Pure leaf so sibling const leaves can reference HTTP_STATUS without the barrel's TDZ cycle.
 *
 * ENFORCEMENT: `QUAL-HTTP-STATUS-NUMERIC-INLINE-001` governs the numeric SSOT bypass, and it is POSITIONAL rather than
 * value-only. The adjudication this replaces said no such rule could exist -- "a bare numeric literal is
 * indistinguishable from ports, limits, counts and fixtures without AST + type context, so a literal ban would be
 * FP-dominant" -- which is true of a matcher that reads the VALUE and nothing else, and was read for months as a
 * standing ban on the whole class. What the rule actually matches is a literal in a STATUS POSITION: assigned to a
 * status field, compared against a status accessor, defaulted from one, listed in a status-named collection, or passed
 * as `jsonResponse`'s status argument. A port, a limit and a count all fail that anchor before the value is consulted;
 * measured day-one queue over the tracked corpus was 10 sites, every one a real status, zero false positives.
 * `QUAL-ERROR-BUILDER-IMPLICIT-STATUS-001` still owns its own narrower drift shape (a `buildAdapterError` call passing
 * NO status argument, so the branch ships the builder's parameter default), which is an ABSENT decision rather than an
 * SSOT bypass -- the site spells no literal for this rule to convict.
 */

const HTTP_STATUS_INFORMATIONAL = { CONTINUE: 100, SWITCHING_PROTOCOLS: 101 } as const;

const HTTP_STATUS_SUCCESS = {
	OK: 200,
	CREATED: 201,
	ACCEPTED: 202,
	NO_CONTENT: 204,
	RESET_CONTENT: 205,
	PARTIAL_CONTENT: 206,
} as const;

const HTTP_STATUS_REDIRECTION = {
	MULTIPLE_CHOICES: 300,
	MOVED_PERMANENTLY: 301,
	FOUND: 302,
	SEE_OTHER: 303,
	NOT_MODIFIED: 304,
	TEMPORARY_REDIRECT: 307,
	PERMANENT_REDIRECT: 308,
} as const;

const HTTP_STATUS_CLIENT_ERROR = {
	BAD_REQUEST: 400,
	UNAUTHORIZED: 401,
	PAYMENT_REQUIRED: 402,
	FORBIDDEN: 403,
	NOT_FOUND: 404,
	METHOD_NOT_ALLOWED: 405,
	NOT_ACCEPTABLE: 406,
	PROXY_AUTHENTICATION_REQUIRED: 407,
	REQUEST_TIMEOUT: 408,
	CONFLICT: 409,
	GONE: 410,
	LENGTH_REQUIRED: 411,
	PRECONDITION_FAILED: 412,
	PAYLOAD_TOO_LARGE: 413,
	URI_TOO_LONG: 414,
	UNSUPPORTED_MEDIA_TYPE: 415,
	RANGE_NOT_SATISFIABLE: 416,
	EXPECTATION_FAILED: 417,
	MISDIRECTED_REQUEST: 421,
	UNPROCESSABLE_ENTITY: 422,
	LOCKED: 423,
	FAILED_DEPENDENCY: 424,
	TOO_EARLY: 425,
	UPGRADE_REQUIRED: 426,
	PRECONDITION_REQUIRED: 428,
	TOO_MANY_REQUESTS: 429,
	REQUEST_HEADER_FIELDS_TOO_LARGE: 431,
	CLIENT_CLOSED_REQUEST: 499,
} as const;

const HTTP_STATUS_SERVER_ERROR = {
	INTERNAL_SERVER_ERROR: 500,
	NOT_IMPLEMENTED: 501,
	BAD_GATEWAY: 502,
	SERVICE_UNAVAILABLE: 503,
	GATEWAY_TIMEOUT: 504,
	HTTP_VERSION_NOT_SUPPORTED: 505,
	VARIANT_ALSO_NEGOTIATES: 506,
	INSUFFICIENT_STORAGE: 507,
	LOOP_DETECTED: 508,
	NOT_EXTENDED: 510,
	NETWORK_AUTHENTICATION_REQUIRED: 511,
} as const;

export const HTTP_STATUS = {
	...HTTP_STATUS_INFORMATIONAL,
	...HTTP_STATUS_SUCCESS,
	...HTTP_STATUS_REDIRECTION,
	...HTTP_STATUS_CLIENT_ERROR,
	...HTTP_STATUS_SERVER_ERROR,
} as const;

/** Keyed `satisfies`, not a `Record<number, string>` annotation, which erased the key set. `Partial` is deliberate -- a curated SUBSET, so a runtime numeric status must be narrowed before indexing. */
export const HTTP_STATUS_TEXT = {
	[HTTP_STATUS.OK]: 'OK',
	[HTTP_STATUS.CREATED]: 'Created',
	[HTTP_STATUS.NO_CONTENT]: 'No Content',
	[HTTP_STATUS.BAD_REQUEST]: 'Bad Request',
	[HTTP_STATUS.UNAUTHORIZED]: 'Unauthorized',
	[HTTP_STATUS.FORBIDDEN]: 'Forbidden',
	[HTTP_STATUS.NOT_FOUND]: 'Not Found',
	[HTTP_STATUS.METHOD_NOT_ALLOWED]: 'Method Not Allowed',
	[HTTP_STATUS.CONFLICT]: 'Conflict',
	[HTTP_STATUS.TOO_MANY_REQUESTS]: 'Too Many Requests',
	[HTTP_STATUS.INTERNAL_SERVER_ERROR]: 'Internal Server Error',
	[HTTP_STATUS.BAD_GATEWAY]: 'Bad Gateway',
	[HTTP_STATUS.SERVICE_UNAVAILABLE]: 'Service Unavailable',
	[HTTP_STATUS.GATEWAY_TIMEOUT]: 'Gateway Timeout',
} as const satisfies Partial<Record<(typeof HTTP_STATUS)[keyof typeof HTTP_STATUS], string>>;

/**
 * THE BAND PREDICATES, HOMED BESIDE THE VOCABULARY THEY COMPOSE. They read nothing but `HTTP_STATUS` above, so the
 * same-file home costs no import edge at all and `@runtime-derived-ssot-helper` arm (a) is TRUE BY CONSTRUCTION. A
 * separate bands leaf could not make that claim: it would have to IMPORT the SSOT rather than derive from a same-file
 * record, so arm (a) would be false there and the classification would rest on arm (c) alone.
 *
 * WHY A `const/` HOME AND NOT A `utils/` ONE, which is the whole reason this is not a style question. Three consumers
 * sit at or below the utils floor and cannot value-import a `utils/` leaf at all
 * (`QUAL-NO-RUNTIME-IMPORTS-IN-UTILS-001`, HIGH, no allowlist): `exitCodeForRunResponse` (`const/cli-flowrunner.ts`),
 * whose CLI entry admits `@federa/const/*` only; `githubPostureResponse`
 * (`utils/testing/github-posture-response-fixture.ts`); and `makeFetchAttempt` (`utils/http/fetch-attempt-core.ts`).
 * A `utils/` home forces each of them either to re-spell the band or to take it as an injected dep, and the tree had
 * one of each. Measured: giving these three symbols a `utils/` home scores one
 * `QUAL-NO-RUNTIME-IMPORTS-IN-UTILS-001` row at `fetch-attempt-core.ts` that the `const/` home scores zero.
 *
 * THE 5xx UPPER BOUND STAYS A LITERAL because no status exists at that value: `HTTP_STATUS` declares nothing there to
 * adopt, and minting a member for a non-status would be the inverse defect. It is module-private, so it is data this
 * leaf holds rather than vocabulary it publishes.
 */
const ABOVE_SERVER_ERROR_BAND = 600;

/** True for a 2xx status.
 *  @runtime-derived-ssot-helper (arm (a) + arm (c)): both band edges are read off the same-file `HTTP_STATUS` record
 *  above and this leaf holds no other decision, so it is a runtime-deriving surface of that SSOT. Arm (c) holds
 *  independently -- `exitCodeForRunResponse` (`const/cli-flowrunner.ts`) is a `const/` consumer and
 *  `githubPostureResponse` (`utils/testing/github-posture-response-fixture.ts`) is a `utils/` one, and neither may
 *  value-import a `utils/` sibling (`QUAL-NO-RUNTIME-IMPORTS-IN-UTILS-001`, HIGH, no allowlist), so relocation arm (b)
 *  is foreclosed for this symbol. */
export function isSuccess(status: number): boolean {
	return status >= HTTP_STATUS.OK && status < HTTP_STATUS.MULTIPLE_CHOICES;
}

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): a headless-browser navigation's HTTP status, or 0 when `goto`
 * answered no response at all (same-document navigation, a hung load) -- 0 sits in no status band, so every band
 * predicate above reads it as "not a success" without a separate null arm. The RealAuction browser agent and its
 * calendar reader each spelled `resp ? resp.status() : 0`.
 */
export function responseStatusOrZero(response: { status(): number } | null | undefined): number {
	return response ? response.status() : 0;
}

/** True for a 3xx status.
 *  @runtime-derived-ssot-helper (arm (a) + arm (c)): both band edges are read off the same-file `HTTP_STATUS` record.
 *  Arm (c): `makeFetchAttempt` (`utils/http/fetch-attempt-core.ts`) value-imports this predicate, and a `utils/` leaf
 *  may not value-import a `utils/` sibling, so a `utils/` home is foreclosed -- that floor is what obliges any
 *  `utils/`-homed spelling of this decision to reach its consumer through an injected `deps.isRedirect` seam. */
export function isRedirect(status: number): boolean {
	return status >= HTTP_STATUS.MULTIPLE_CHOICES && status < HTTP_STATUS.BAD_REQUEST;
}

/** True for a 4xx status: the far side READ the request and refused it, which is the one failure class whose outcome
 *  is known -- nothing was done. A 5xx or a dropped connection says no such thing.
 *  @runtime-derived-ssot-helper (arm (a)): both band edges are read off the same-file `HTTP_STATUS` record, the same
 *  way its two neighbours read theirs. */
export function isClientError(status: number): boolean {
	return status >= HTTP_STATUS.BAD_REQUEST && status < HTTP_STATUS.INTERNAL_SERVER_ERROR;
}

/** True for a 5xx status. BOUNDED ABOVE at the end of the status space -- an open-ended `>= INTERNAL_SERVER_ERROR`
 *  test is a DIFFERENT predicate that coincides on 500-599 and diverges above it, so the two are not interchangeable.
 *  @runtime-derived-ssot-helper (arm (a) + arm (c)): the lower edge is read off the same-file `HTTP_STATUS` record and
 *  the upper off the same-file `ABOVE_SERVER_ERROR_BAND`. Arm (c): `makeFetchAttempt`
 *  (`utils/http/fetch-attempt-core.ts`) value-imports it under the same utils-sibling floor as `isRedirect`. */
export function isServerError(status: number): boolean {
	return status >= HTTP_STATUS.INTERNAL_SERVER_ERROR && status < ABOVE_SERVER_ERROR_BAND;
}

/** True for 401 or 403: the far side judged the CREDENTIAL itself and refused it -- the one refusal a sign-in, a token
 *  or a token's scope can explain. A 5xx, a transport failure or any other 4xx says nothing about the credential.
 *  @runtime-derived-ssot-helper (arm (a)): both statuses are read off the same-file `HTTP_STATUS` record. */
export function isCredentialRefusal(status: number): boolean {
	return status === HTTP_STATUS.UNAUTHORIZED || status === HTTP_STATUS.FORBIDDEN;
}
